import React, { useState, useEffect, useRef } from "react";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Typography,
  Paper,
  Button,
  MenuItem,
  TextField,
  Stack,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatIcon from '@mui/icons-material/Chat';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupIcon from '@mui/icons-material/Group';
import CampaignIcon from '@mui/icons-material/Campaign';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import { api } from '../../services/auth';
import { analyzeCampaignPerformance } from '../../services/chatService';
import { fetchLLMConfig } from '../../services/config';
import axios from 'axios';

// États et gestionnaires d'erreurs
const getStatusColor = (status) => {
  switch (status) {
    case 'en_cours':
      return '#2196f3';  // Bleu
    case 'echec':
      return '#f44336';  // Rouge
    case 'terminee':
      return '#4caf50';  // Vert
    case 'inactive':
      return '#9e9e9e';  // Gris
    default:
      return '#9e9e9e';  // Gris
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'en_cours':
      return 'En cours';
    case 'echec':
      return 'Échec';
    case 'terminee':
      return 'Terminée';
    case 'inactive':
      return 'Inactive';
    default:
      return 'Inactive';
  }
};

// Fonction pour déterminer le statut métier d'une campagne
const getCampagneStatus = (c) => {
  const now = new Date();
  const publishUp = c.publishUp ? new Date(c.publishUp) : null;
  const publishDown = c.publishDown ? new Date(c.publishDown) : null;
  if (!c.isPublished) return { label: 'Inactive', color: '#b71c1c' };
  // Cas spécial : publiée, pas de dates => en cours sans date de fin
  if (c.isPublished && !publishUp && !publishDown) {
    return { label: 'En cours (pas de date de fin, arrêt manuel)', color: '#9c27b0' }; // violet
  }
  if (publishUp && publishUp > now) return { label: 'En attente', color: '#ff9800' };
  if (publishDown && publishDown < now) return { label: 'Terminée', color: '#4caf50' };
  // En cours sans date de fin (publishUp <= now, publishDown == null)
  if (publishUp && publishUp <= now && !publishDown) {
    return { label: 'En cours (pas de date de fin, arrêt manuel)', color: '#9c27b0' }; // violet
  }
  // En cours classique (publishUp <= now, publishDown > now)
  if (publishUp && publishUp <= now && publishDown && publishDown > now) {
    return { label: 'En cours', color: '#2196f3' };
  }
  return { label: 'Inconnu', color: '#9e9e9e' };
};

const FormattedAnalysisText = ({ text }) => {
  if (!text || text === 'Non disponible') {
    return <Typography color="text.secondary">Non disponible</Typography>;
  }

  return (
    <Typography
      sx={{
        textAlign: 'justify',
        whiteSpace: 'pre-line',
        fontSize: '1rem',
        lineHeight: 1.7,
        color: '#495057'
      }}
    >
      {text
        .replace(/^\s*- /gm, '• ') // Ne transforme en puce que les tirets en début de ligne
        .split(/(?<=\.)\s+(?=[A-Z•])/)
        .map((line, idx) => (
          <React.Fragment key={idx}>
            {line.trim()}
            <br />
          </React.Fragment>
        ))}
    </Typography>
  );
};


function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function AssistanceCampagne() {
  const [selected, setSelected] = useState("");
  const isInitialLoad = useRef(true);
  const [campaigns, setCampaigns] = useState([]);
  const [iaAnalysis, setIaAnalysis] = useState(null);
  const [iaAnalysisLoading, setIaAnalysisLoading] = useState(false);
  const [iaAnalysisError, setIaAnalysisError] = useState(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const navigate = useNavigate();
  const query = useQuery();
  const campaignNameFromQuery = query.get('campaignName');

  const handleSelectChange = (event) => {
    const newSelectedCampaign = event.target.value;
    setSelected(newSelectedCampaign);
  };

  // Effet pour charger les campagnes et gérer la sélection initiale depuis l'URL
  useEffect(() => {
    fetchCampaigns();
    if (campaignNameFromQuery) {
      setSelected(campaignNameFromQuery);
    }
  }, [campaignNameFromQuery]);

  const fetchCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await api.get('/campaigns');
      if (response.data && response.data.success && Array.isArray(response.data.campaigns)) {
        setCampaigns(response.data.campaigns);
      } else {
        setCampaigns([]);
        console.error('Format de réponse inattendu:', response.data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des campagnes:', error);
      setCampaigns([]);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    const campaignNameFromUrl = query.get('campaignName');
    // This effect should only run once when campaigns are loaded and a URL parameter is present.
    if (campaignNameFromUrl && campaigns.length > 0 && isInitialLoad.current) {
      const campaignToAnalyze = campaigns.find(c => c.name === campaignNameFromUrl);
      if (campaignToAnalyze) {
        isInitialLoad.current = false; // Prevent this logic from ever running again
        setSelected(campaignNameFromUrl); // Update the dropdown to reflect the analyzed campaign

        const status = getCampagneStatus(campaignToAnalyze);
        if (status.label === 'Terminée') {
          setIaAnalysisError('Une campagne terminée ne peut pas être analysée.');
          return; // Stop here if completed
        }
        
        // Directly call analysis for the campaign from the URL
        handleAnalysis(campaignNameFromUrl);
      }
    }
  }, [campaigns, query]); // Reruns if campaigns load or query params change

  const handleAnalysis = async (campaignNameToAnalyze) => {
    // If a campaign name is passed as a string, use it (for auto-analysis).
    // Otherwise, use the 'selected' state (for manual button clicks).
    const campaignName = typeof campaignNameToAnalyze === 'string' ? campaignNameToAnalyze : selected;
    const campaignToAnalyze = campaigns.find(c => c.name === campaignName);
    if (!campaignToAnalyze) return;

    const controller = new AbortController();
    setIaAnalysisLoading(true);
    setIaAnalysis(null);
    setIaAnalysisError(null);

    try {
      // Fetch LLM configuration from backend
      const llmConfig = await fetchLLMConfig();
      console.log('[ASSISTANCE] Using LLM config:', llmConfig);
      
      const analysis = await analyzeCampaignPerformance(
        `Analyse complète de la performance de la campagne "${campaignToAnalyze.name}"`,
        { provider: llmConfig.provider, model: llmConfig.model, signal: controller.signal }
      );

      if (controller.signal.aborted) return;

      const analysisText = analysis?.campaign_analysis_text || '';
      const cleanedText = analysisText.replace(/Cordialement,.*$/s, '').replace(/L'équipe de marketing.*$/s, '');

      const sections = {
        evaluation: cleanedText.match(/(1\.\s*)?(Evaluation globale|Analyse globale)[\s\S]*?(?=\n\n\d\.|$)/s)?.[0] || 'Non disponible',
        indicateurs: cleanedText.match(/(2\.\s*)?(Analyse des indicateurs clés|Indicateurs clés|Statistiques clés)[\s\S]*?(?=\n\n\d\.|$)/s)?.[0] || 'Non disponible',
        benchmarks: cleanedText.match(/(3\.\s*)?(Comparaison aux benchmarks|Benchmark)["\s\S]*?(?=\n\n\d\.|$)/s)?.[0] || 'Non disponible',
        recommandations: cleanedText.match(/(5\.\s*)?(Recommandations)[\s\S]*?(?=\n\n\d\.|$)/s)?.[0] || 'Non disponible',
        pointsSection: cleanedText.match(/(6\.\s*)?(Points forts et points faibles|Points forts et faibles|Forces et Faiblesses|Identification)[\s\S]*?$/s)?.[0] || 'Non disponible'
      };

      const cleanText = (text) => {
        if (!text || text === 'Non disponible') return 'Non disponible';
        let cleaned = text.replace(/^\d+\.\s*.*\n/, '').trim();
        if (cleaned === text.trim()) {
          cleaned = cleaned.replace(/^\d+\.\s*/, '').trim();
        }
        const titles = ["Evaluation globale", "Analyse globale", "Indicateurs clés", "Recommandations", "Points forts et points faibles","Comparaison aux benchmarks du secteur"];
        const titleRegex = new RegExp(`^(${titles.join('|')})\\s*[:\\-]?\\s*`, 'i');
        cleaned = cleaned.replace(titleRegex, '').trim().replace(/\* /g, '\n• ');
        return cleaned;
      };

      const analysisData = {
        evaluation: cleanText(sections.evaluation),
        indicateurs: cleanText(sections.indicateurs),
        benchmarks: cleanText(sections.benchmarks),
        recommandations: cleanText(sections.recommandations),
        pointsSection: cleanText(sections.pointsSection),
        statistics: analysis.statistics
      };

      setIaAnalysis(analysisData);

    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Requête d\'analyse annulée proprement.');
      } else if (!controller.signal.aborted) {
        setIaAnalysisError('Une erreur est survenue lors de l\'analyse.');
      }
    } finally {
      if (!controller.signal.aborted) {
        setIaAnalysisLoading(false);
      }
    }
  };

  const handleValidate = () => {
    navigate('/campagne/planification', {
      state: {
        campaignName: selected,
        suggestions: iaAnalysis?.recommandations ? iaAnalysis.recommandations.split('\n').filter(s => s.length > 0) : []
      }
    });
  };

  const handleCustomize = () => {
    if (!selected || !iaAnalysis?.recommandations) return;
    const prompt = `Peux-tu m'aider à améliorer la campagne marketing nommée '${selected}' en te basant sur les points d'amélioration suivants :\n${iaAnalysis.recommandations}`;
    navigate('/chat', {
      state: {
        prompt: prompt
      }
    });
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" minHeight="90vh" bgcolor="#f8f9fa" p={2}>
      <Paper elevation={2} sx={{ width: 800, maxWidth: '100%', p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} color="#1976d2" mb={2} fontFamily="Montserrat, Arial, sans-serif">
          Optimisation de Campagne Assistée par IA
        </Typography>

        {campaignNameFromQuery && isLoadingCampaigns ? (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 2, p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            Préparation de l'analyse pour "{campaignNameFromQuery}"...
          </Box>
        ) : (
          <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              fullWidth
              label="Choisir une campagne"
              value={selected}
              onChange={handleSelectChange}
              disabled={iaAnalysisLoading}
              sx={{ bgcolor: 'white' }}
            >
              {isLoadingCampaigns ? (
                <MenuItem value="" disabled>Chargement...</MenuItem>
              ) : (
                [
                  <MenuItem key="placeholder" value="" disabled>-- Choisir une campagne --</MenuItem>,
                  ...campaigns.map(c => (
                    <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                  ))
                ]
              )}
            </TextField>
            <Button
              variant="contained"
              onClick={handleAnalysis}
              disabled={!selected || iaAnalysisLoading}
              startIcon={<OnlinePredictionIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Analyser & suggérer des optimisations
            </Button>
          </Stack>
        )}

        {/* Le bouton n'est plus nécessaire, l'analyse se lance automatiquement */}

        {iaAnalysisLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8, // Espace vertical généreux
              textAlign: 'center',
              border: '1px dashed #e0e0e0',
              borderRadius: 2,
              bgcolor: '#fafafa',
              m: 2
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Analyse en cours...
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Veuillez patienter, l'IA évalue les performances de la campagne.
            </Typography>
          </Box>
        )}

        {/* Résultats de l'analyse */}
        <Collapse in={iaAnalysis && !iaAnalysisLoading}>
          {selected && iaAnalysis && (
            <>
              {/* Statistiques actuelles */}
              <Box mb={3}>
                <Typography variant="h5" fontWeight={600}  mb={2} color="#2c3e50">
                  Statistiques actuelles
                </Typography>
                <Stack direction="row" spacing={3} mb={2}>
                  <Paper elevation={1} sx={{ p: 2, flex: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography color="text.secondary" fontSize={14}>Taux d'ouverture</Typography>
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {iaAnalysis?.indicateurs?.match(/Taux d'ouverture.*?\%/)?.[0] || 'Non disponible'}
                    </Typography>
                  </Paper>
                  <Paper elevation={1} sx={{ p: 2, flex: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography color="text.secondary" fontSize={14}>Taux de clic</Typography>
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {iaAnalysis?.indicateurs?.match(/Taux de clic.*?\%/)?.[0] || 'Non disponible'}
                    </Typography>
                  </Paper>
                  <Paper elevation={1} sx={{ p: 2, flex: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography color="text.secondary" fontSize={14}>Conversion</Typography>
                    <Typography variant="h5" color="primary" fontWeight={700}>
                      {iaAnalysis?.indicateurs?.match(/Conversion.*?\%/)?.[0] || 'Non disponible'}
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              {/* Analyse détaillée */}
              <Box mb={3}>
                <Typography variant="h5" fontWeight={600} mb={2} color="#2c3e50">
                  Analyse détaillée
                </Typography>
                <List>
                  <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 1 }}>
                    <ListItemText
                      primary="Evaluation globale"
                      primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '1.1rem' } }}
                      secondary={<FormattedAnalysisText text={iaAnalysis?.evaluation} />}
                      secondaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                  <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 1 }}>
                    <ListItemText
                      primary="Indicateurs clés"
                      primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '1.1rem' } }}
                      secondary={<FormattedAnalysisText text={iaAnalysis?.indicateurs} />}
                      secondaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                  <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 1 }}>
                    <ListItemText
                      primary="Comparaison aux benchmarks"
                      primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '1.1rem' } }}
                      secondary={<FormattedAnalysisText text={iaAnalysis?.benchmarks} />}
                      secondaryTypographyProps={{ sx: { fontSize: '1rem', lineHeight: 1.6 } }}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                  <ListItem sx={{ display: 'block', bgcolor: '#f8f9fa', borderRadius: 2, mb: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 1 }}>
                      Aperçu des performances des e-mails individuels
                    </Typography>
                    {iaAnalysis?.statistics ? (
                      (() => {
                        const stats = iaAnalysis.statistics;
                        const emailStats = stats.email_statistics?.per_email_stats || {};
                        const clickStats = stats.click_statistics?.per_email_clicks || {};
                        const emailData = Object.keys(emailStats).map(emailId => {
                          const email = emailStats[emailId];
                          const clicks = clickStats[emailId];
                          const sent = email.total_sent || 0;
                          const openRate = sent > 0 ? ((email.unique_opens / sent) * 100).toFixed(2) + '%' : '0.00%';
                          const clickRate = sent > 0 && clicks ? ((clicks.total_clicks / sent) * 100).toFixed(2) + '%' : '0.00%';
                          const unsubscribeRate = sent > 0 ? ((email.total_unsubscribes / sent) * 100).toFixed(2) + '%' : '0.00%';
                          return {
                            id: emailId,
                            name: email.email_name || `Email ID: ${emailId}`,
                            openRate,
                            clickRate,
                            unsubscribeRate
                          };
                        });

                        if (emailData.length === 0) {
                          return <Typography sx={{ mt: 1, fontStyle: 'italic' }}>Aucune statistique détaillée par email disponible.</Typography>;
                        }

                        return (
                          <TableContainer component={Paper} sx={{ mt: 2, border: '1px solid rgba(224, 224, 224, 1)', borderRadius: 2 }}>
                            <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 1)' } }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>Email</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>Taux d'ouverture (unique)</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>Taux de clics</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>Taux de désabonnement</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {emailData.map((row) => (
                                  <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ fontSize: '0.9rem' }}>{row.name}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.9rem' }}>{row.openRate}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.9rem' }}>{row.clickRate}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.9rem' }}>{row.unsubscribeRate}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        );
                      })()
                    ) : (
                      <FormattedAnalysisText text={iaAnalysis?.apercu} />
                    )}
                  </ListItem>
                  
                </List>
              </Box>

              {/* Suggestions */}
              <Box mb={3}>
                <Typography variant="h5" fontWeight={600} mb={2} color="#2c3e50">
                  Suggestions d'optimisation
                </Typography>
                <List>
                <ListItemText
  primary="Recommandations"
  primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '1.1rem' } }}
  secondary={
    <Typography
      sx={{
        textAlign: 'justify',
        whiteSpace: 'pre-line',
        fontSize: '1rem',
        lineHeight: 1.7
      }}
    >
      {(iaAnalysis?.recommandations || 'Non disponible')
        .replace(/(?<!\n)- /g, '\n- ') // saut de ligne avant chaque tiret
        .split('\n')
        .map((line, idx) => (
          <React.Fragment key={idx}>
            {line.trim()}
            {idx < (iaAnalysis?.recommandations?.length || 0) - 1 && <br />}
          </React.Fragment>
        ))}
    </Typography>
  }
/>

                </List>
              </Box>

              {/* Points forts et faibles */}
              <Box mb={3}>
                <Typography variant="h5" fontWeight={600} mb={2} color="#2c3e50">
                  Points forts et faibles
                </Typography>
                <List>
                <ListItem sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 1, display: 'block' }}>
  <Typography fontWeight={600} fontSize="1.1rem" mb={1}>
    Points forts et points faibles
  </Typography>
  {(iaAnalysis?.pointsSection || 'Non disponible')
    .replace(/(?<!\n)- /g, '\n• ')
    .replace(/Points faibles:/g, '\nPoints faibles:')
    .replace(/Points forts:/g, '\nPoints forts:')
    .split('\n')
    .map((line, idx) => {
      const trimmedLine = line.trim();

      // Titre de section
      if (trimmedLine.toLowerCase() === 'points forts:') {
        return (
          <Typography
            key={idx}
            fontWeight={600}
            sx={{ color: '#2e7d32', mb: 0.5 }}
          >
            <CheckCircleIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
            Points forts:
          </Typography>
        );
      }

      if (trimmedLine.toLowerCase() === 'points faibles:') {
        return (
          <Typography
            key={idx}
            fontWeight={600}
            sx={{ color: '#e65100', mt: 1, mb: 0.5 }}
          >
            <WarningAmberIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
            Points faibles:
          </Typography>
        );
      }

      // Élément listé avec puce
      if (trimmedLine.startsWith('•')) {
        return (
          <Box key={idx} display="flex" alignItems="flex-start" mb={0.5}>
            <Box sx={{ width: 24 }}>
              {/* Icône neutre pour tous les éléments listés */}
              <Typography sx={{ fontSize: 16, color: '#90a4ae' }}>•</Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '1rem',
                lineHeight: 1.6,
                color: '#495057',
                textAlign: 'justify'
              }}
            >
              {trimmedLine.replace(/^•\s*/, '')}
            </Typography>
          </Box>
        );
      }

      // Ligne normale
      return (
        <Typography key={idx} sx={{ fontSize: '1rem', color: '#495057', textAlign: 'justify' }}>
          {trimmedLine}
        </Typography>
      );
    })}
</ListItem>



                </List>
              </Box>

              {/* Actions */}
              {iaAnalysisError && (
                <Typography color="error" sx={{ width: '100%', mt: 2, textAlign: 'center' }}>
                  {iaAnalysisError}
                </Typography>
              )}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ChatIcon />}
                  onClick={handleCustomize}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  Personnaliser avec le Chat
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleValidate}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  Valider et Planifier
                </Button>
              </Stack>
            </>
          )}
        </Collapse>
      </Paper>

      {/* Bouton retour */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mt: 3 }}
      >
        Retour
      </Button>
    </Box>
  );
}
